import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Boîte de notifications interne.
 *
 * Second canal, à côté de l'email, et le seul consultable après coup : un email
 * supprimé ou classé en indésirable ne laisse aucune trace dans l'application.
 * C'est aussi le seul qui fonctionne sans configuration extérieure — ni clé
 * d'API, ni DNS, ni passerelle de filtrage.
 *
 * Le compteur est rafraîchi périodiquement plutôt que poussé : à cette
 * volumétrie — quelques dizaines de litiges par mois — un canal temps réel
 * serait une infrastructure à maintenir pour un problème qui n'existe pas.
 */
const INTERVALLE_MS = 60_000;

function dateLisible(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 1440) return `il y a ${Math.round(minutes / 60)} h`;
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

export default function NotificationBell() {
  const [ouvert, setOuvert] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [nonLues, setNonLues] = useState(0);
  const navigate = useNavigate();

  const rafraichirCompteur = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setNonLues(data.count || 0);
    } catch {
      // Une boîte injoignable ne doit pas faire clignoter l'en-tête : on garde
      // le dernier compteur connu.
    }
  }, []);

  useEffect(() => {
    rafraichirCompteur();
    const minuteur = setInterval(rafraichirCompteur, INTERVALLE_MS);
    return () => clearInterval(minuteur);
  }, [rafraichirCompteur]);

  useEffect(() => {
    if (!ouvert) return;
    api.get("/notifications", { params: { limit: 20 } })
      .then(({ data }) => setNotifications(data || []))
      .catch(() => setNotifications([]));
  }, [ouvert]);

  const ouvrir = async (notification) => {
    setOuvert(false);
    if (!notification.read_at) {
      try {
        await api.post(`/notifications/${notification.id}/read`);
        setNonLues((n) => Math.max(0, n - 1));
      } catch {
        // Le marquage manqué n'empêche pas d'ouvrir le dossier : c'est ce que
        // la personne voulait faire.
      }
    }
    if (notification.link) navigate(notification.link);
  };

  const toutMarquer = async () => {
    try {
      await api.post("/notifications/read-all");
      setNonLues(0);
      setNotifications((liste) =>
        liste.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })),
      );
    } catch {
      /* le compteur sera corrigé au prochain rafraîchissement */
    }
  };

  return (
    <Popover open={ouvert} onOpenChange={setOuvert}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="hidden sm:inline-flex p-2 rounded-xl hover:bg-white/10 transition-colors relative"
          data-testid="notifications-btn"
          aria-label={
            nonLues > 0 ? `Notifications, ${nonLues} non lue(s)` : "Notifications"
          }
        >
          <Bell size={18} strokeWidth={1.75} />
          {nonLues > 0 ? (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-rose-500 text-[10px] font-bold leading-[1.1rem] text-white"
              data-testid="notifications-count"
            >
              {nonLues > 99 ? "99+" : nonLues}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[22rem] p-0" align="end">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <span className="text-sm font-semibold text-slate-900">Notifications</span>
          {nonLues > 0 ? (
            <button
              type="button"
              onClick={toutMarquer}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50"
              data-testid="notifications-read-all"
            >
              <CheckCheck size={13} /> Tout marquer lu
            </button>
          ) : null}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notifications-empty">
              Aucune notification.
            </p>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.id}
                type="button"
                onClick={() => ouvrir(notification)}
                className={cn(
                  "flex w-full gap-2.5 border-b border-slate-50 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50",
                  !notification.read_at && "bg-blue-50/40",
                )}
                data-testid={`notification-${notification.id}`}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                    notification.read_at ? "bg-transparent" : "bg-blue-600",
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-900">
                    {notification.title}
                  </span>
                  {notification.body ? (
                    <span className="mt-0.5 block line-clamp-2 text-xs text-slate-600">
                      {notification.body}
                    </span>
                  ) : null}
                  <span className="mt-1 block text-[11px] text-slate-400">
                    {dateLisible(notification.created_at)}
                  </span>
                </span>
                {notification.read_at ? (
                  <Check size={13} className="mt-1 shrink-0 text-slate-300" />
                ) : null}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
